import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FiPrinter,
  FiDownload,
  FiRotateCcw,
  FiTruck,
  FiCheckCircle,
  FiAlertTriangle,
  FiFileText,
  FiAlertCircle,
  FiTag,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { printBarcodeLabels } from "../../utils/printBarcodeLabels";
import { grnDetailTotals } from './grnDetailTotals';
import { hasPermission } from '../../utils/permissions';
import PostedGrnGrid from './PostedGrnGrid';
import GrnEditModal from './GrnEditModal';

const statusStyles = {
  DRAFT: "bg-orange-100 text-orange-700",
  POSTED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};

export default function GRNDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken, user } = useAuth();
  const [posting, setPosting] = useState(false);
  const postLock = useRef(false);

  const [grn, setGrn] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [labelError, setLabelError] = useState("");
  const [showExtendedItems, setShowExtendedItems] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    async function loadGrn() {
      try {
        setIsLoading(true);
        setError("");

        const response = await apiRequest(`/purchases/grns/${id}`, {
          accessToken,
        });

        setGrn(response);
      } catch (requestError) {
        setError(requestError.message || "Could not load GRN.");
      } finally {
        setIsLoading(false);
      }
    }

    if (accessToken && id) loadGrn();
  }, [accessToken, id]);

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    })}`;

  const currencyOrDash = (value) =>
    value === null || value === undefined ? "—" : formatCurrency(value);

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const formatPercent = (value) =>
    value === null || value === undefined ? "—" : `${Number(value).toFixed(2)}%`;

  const totals = useMemo(() => grnDetailTotals(grn || {}), [grn]);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-100 border-t-primary-800" />
      </div>
    );
  }

  if (!grn) {
    return (
      <div className="rounded-3xl border border-red-100 bg-red-50 p-6 text-red-700">
        {error || "GRN not found."}
      </div>
    );
  }

  const canReturn = grn.status === "POSTED";
  const address = entity => [entity?.addressLine1, entity?.addressLine2, entity?.city, entity?.state, entity?.postalCode, entity?.country].filter(Boolean).join(', ');
  const postReceipt = async () => {
    if (postLock.current) return;
    postLock.current = true;
    setPosting(true);
    setError('');
    try {
      await apiRequest(`/purchases/grns/${id}/post`, {method:'POST',accessToken});
      setGrn(await apiRequest(`/purchases/grns/${id}`, {accessToken}));
    } catch (requestError) { setError(requestError.message || 'Could not post this GRN.'); }
    finally { setPosting(false); postLock.current = false; }
  };
  const printLabels = () => {
    try {
      setLabelError("");
      printBarcodeLabels(grn.items || []);
    } catch (printError) {
      setLabelError(printError.message || "Could not prepare barcode labels.");
    }
  };

  return (
    <div>
      {editing && <GrnEditModal grn={grn} accessToken={accessToken} onClose={() => setEditing(false)} onSaved={updated => { setGrn(updated); setEditing(false); setError(''); }}/>}
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Purchasing / GRN</p>

          <div className="mt-1 flex items-center gap-4">
            <h1 className="text-3xl font-bold">{grn.grnNumber}</h1>

            <span className={`rounded-full px-3 py-1 text-sm font-semibold ${statusStyles[grn.status] || "bg-gray-100 text-gray-700"}`}>
              {grn.status}
            </span>
          </div>

          <p className="mt-2 text-gray-500">
            Received on {formatDate(grn.receivedAt)}
          </p>
          <p className="mt-1 text-sm text-gray-500">Supplier invoice: {grn.supplierInvoiceNumber || "—"} · Invoice date: {formatDate(grn.supplierInvoiceDate)}</p>
        </div>

        <div className="flex flex-wrap gap-3">
          {['DRAFT','POSTED'].includes(grn.status) && hasPermission(user, 'purchases.update') && <button disabled={posting} type="button" onClick={() => setEditing(true)} className="h-11 rounded-xl border border-primary-200 px-5 font-semibold text-primary-800">Edit GRN</button>}
          {grn.status === 'DRAFT' && hasPermission(user, 'purchases.receive') && (
            <button type="button" disabled={posting} onClick={postReceipt} className="flex h-11 items-center gap-2 rounded-xl bg-primary-800 px-5 font-semibold text-white disabled:opacity-50"><FiTruck />{posting ? 'Posting…' : 'Post receipt'}</button>
          )}
          <ActionButton
            icon={<FiPrinter />}
            label="Print"
            onClick={() => window.print()}
          />

          <ActionButton
            icon={<FiDownload />}
            label="Save PDF"
            onClick={() => window.print()}
          />

          {grn.status === "POSTED" && (
            <ActionButton icon={<FiTag />} label="Print barcode labels" onClick={printLabels} />
          )}

          {canReturn && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/admindashboard/purchases/returns/create?goodsReceiptId=${grn.id}`,
                )
              }
              className="flex h-11 items-center gap-2 rounded-xl bg-red-50 px-5 text-red-600"
            >
              <FiRotateCcw />
              Supplier Return
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {labelError && <div className="mb-6 flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700"><FiAlertCircle className="mt-0.5 shrink-0" /><p>{labelError}</p></div>}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <Card title="PO Number" value={grn.purchaseOrder?.purchaseOrderNumber} />
        <Card title="Supplier" value={grn.supplier?.displayName} />
        <Card title="Agreement" value={grn.agreementType || 'Legacy — not classified'} />
        <Card title="Received By" value={grn.postedBy?.email || grn.createdBy?.email} />
        <Card title="Payable after round-off" value={formatCurrency(totals.payable)} />
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="mb-5 text-lg font-semibold">Supplier Details</h2>

          <div className="space-y-4">
            <Info label="Supplier" value={grn.supplier?.displayName} />
            <Info label="Legal name" value={grn.supplier?.legalName} />
            <Info label="Supplier code" value={grn.supplier?.code} />
            <Info label="Invoice Number" value={grn.supplierInvoiceNumber} />
            <Info label="Invoice Date" value={formatDate(grn.supplierInvoiceDate)} />
            <Info label="PO Number" value={grn.purchaseOrder?.purchaseOrderNumber} />
            <Info label="GST Number" value={grn.supplier?.gstin} />
            <Info label="PAN" value={grn.supplier?.pan} />
            <Info label="Phone / Email" value={[grn.supplier?.phone, ...(grn.supplier?.additionalPhones || []), grn.supplier?.email].filter(Boolean).join(' · ')} />
            <Info label="Address" value={address(grn.supplier)} />
          </div>
        </div>

        <div className="rounded-3xl border border-gray-100 bg-white p-6">
          <h2 className="mb-5 text-lg font-semibold">Receiving Details</h2>

          <div className="space-y-4">
            <Info label="Store / Warehouse" value={grn.store?.name} />
            <Info label="Store Code" value={grn.store?.code} />
            <Info label="Branch" value={grn.store?.branch?.name} />
            <Info label="Receiving address" value={address(grn.store)} />
            <Info label="Season" value={grn.season?.name} />
            <Info label="Received Date" value={formatDate(grn.receivedAt)} />
            <Info label="Posted Date" value={formatDate(grn.postedAt)} />
            <Info label="Freight (excl. GST)" value={formatCurrency(grn.freightAmount)} />
            <Info label="Freight GST" value={formatPercent(grn.freightTaxRate)} />
            <Info label="Freight GST amount" value={formatCurrency(grn.freightTaxAmount)} />
            <Info label="Created / Posted by" value={[grn.createdBy?.email, grn.postedBy?.email].filter(Boolean).join(' / ')} />
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-primary-100 bg-primary-50 px-6 py-4 text-sm text-primary-900">
        {grn.status === 'POSTED' ? 'Stock has been posted. The lot shown on each line is the actual receiving lot.' : 'Draft only: no stock added yet. On posting, matching store/variant/season/financial-year and cost/price lots are reused; otherwise a new lot is created.'}
      </div>

      {grn.status === 'POSTED' && <><PostedGrnGrid grn={grn} /><button type="button" onClick={()=>setShowExtendedItems(value=>!value)} className="mb-6 rounded-xl border border-primary-200 bg-white px-4 py-2 text-sm font-semibold text-primary-800">{showExtendedItems ? 'Hide extended item details' : 'Show extended item details'}</button></>}
      {(grn.status !== 'POSTED' || showExtendedItems) && <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-6">
          <h2 className="text-lg font-semibold">Received Products</h2>
        </div>

        <div className="max-h-[65vh] overflow-auto">
          <table className="w-full min-w-[4400px] border-collapse text-sm [&_th]:border [&_th]:border-primary-700 [&_td]:border [&_td]:border-gray-200">
            <thead>
              <tr className="sticky top-0 z-10 bg-primary-800 text-left text-[11px] uppercase tracking-wide text-white">
                <th className="px-4 py-4">S.No.</th>
                <th className="px-4 py-4">Article No</th>
                <th className="px-4 py-4">Product / variant</th>
                <th className="px-4 py-4">Brand</th>
                <th className="px-4 py-4">Category</th>
                <th className="px-4 py-4">Sub category</th>
                <th className="px-4 py-4">Season</th>
                <th className="px-4 py-4">Unit</th>
                <th className="px-4 py-4">Size group</th>
                <th className="px-4 py-4">Colour / size</th>
                <th className="px-4 py-4">SKU</th>
                <th className="px-4 py-4">CHARMS barcode</th>
                <th className="px-4 py-4">Supplier / EAN</th>
                <th className="px-4 py-4">HSN</th>
                <th className="px-4 py-4">GST rule</th>
                <th className="px-4 py-4">Lot / batch</th>
                <th className="px-4 py-4">Lot action</th>
                <th className="px-4 py-4">Supplier batch</th>
                <th className="px-4 py-4">Financial year / expiry</th>
                <th className="px-4 py-4">Ordered</th>
                <th className="px-4 py-4">Received</th>
                <th className="px-4 py-4">Accepted</th>
                <th className="px-4 py-4">Rejected</th>
                <th className="px-4 py-4">Basic rate</th>
                <th className="px-4 py-4">C.D. % / amount</th>
                <th className="px-4 py-4">T.D. % / amount</th>
                <th className="px-4 py-4">SPCD % / amount</th>
                <th className="px-4 py-4">Total discount</th>
                <th className="px-4 py-4">Net rate</th>
                <th className="px-4 py-4">MRP</th>
                <th className="px-4 py-4">POS price</th>
                <th className="px-4 py-4">Online price</th>
                <th className="px-4 py-4">CGST</th>
                <th className="px-4 py-4">SGST</th>
                <th className="px-4 py-4">IGST</th>
                <th className="px-4 py-4">GST applied</th>
                <th className="px-4 py-4">GST amount</th>
                <th className="px-4 py-4">Line total incl. GST</th>
                <th className="px-4 py-4">Inventory unit cost</th>
                <th className="px-4 py-4">Inventory impact</th>
              </tr>
            </thead>

            <tbody>
              {grn.items.map((item, index) => (
                <tr key={item.id} className="border-t border-gray-100">
                  <DetailCell value={index + 1} />
                  <td className="px-4 py-4 font-mono text-xs">{item.variant?.product?.productCode || "—"}</td>
                  <td className="px-4 py-4">
                    <h4 className="font-medium">
                      {item.variant?.product?.name || "—"}
                      {item.variant?.variantName
                        ? ` — ${item.variant.variantName}`
                        : ""}
                    </h4>
                    <p className="text-xs text-gray-500">{item.variant?.variantName || "Default variant"}</p>
                    {item.variant?.attributes?.originalGroupName && <p className="mt-1 text-xs text-gray-500">PT group: {item.variant.attributes.originalGroupName}</p>}
                  </td>
                  <DetailCell value={item.variant?.product?.brand?.name} />
                  <DetailCell value={item.variant?.product?.category?.name} />
                  <DetailCell value={item.variant?.product?.subCategory?.name} />
                  <DetailCell value={grn.season?.name || item.variant?.product?.season?.name} />
                  <DetailCell value={item.variant?.product?.unit?.name} />
                  <DetailCell value={item.variant?.size?.sizeGroup?.name} />
                  <DetailCell value={[item.variant?.color?.name, item.variant?.size?.name].filter(Boolean).join(" / ")} />
                  <DetailCell value={item.variant?.sku} mono />
                  <DetailCell value={item.variant?.barcode} mono />
                  <DetailCell value={item.variant?.supplierBarcode} mono />
                  <DetailCell value={item.variant?.product?.hsnCode} mono />
                  <DetailCell value={item.variant?.product?.gstSlab?.ruleGroup} />
                  <DetailCell value={item.inventoryLot?.lotNumber || 'Assigned on posting'} mono />
                  <DetailCell value={item.inventoryLot ? (item.inventoryLot.originGoodsReceiptId === grn.id ? 'New lot' : 'Existing lot reused') : 'Awaiting posting'} />
                  <DetailCell value={item.batchNumber} mono />
                  <DetailCell value={[item.inventoryLot?.financialYear, item.expiryDate ? formatDate(item.expiryDate) : null].filter(Boolean).join(' / ')} />
                  <DetailCell value={item.purchaseOrderItem?.quantityOrdered} />
                  <DetailCell value={item.quantityReceived} />
                  <DetailCell value={item.quantityAccepted} strong="text-green-600" />
                  <DetailCell value={item.quantityRejected} strong="text-red-600" />
                  <DetailCell value={formatCurrency(item.unitCost)} />
                  <DetailCell value={`${formatPercent(item.cashDiscountPercent)} / ${formatCurrency(item.cashDiscountAmount)}`} />
                  <DetailCell value={`${formatPercent(item.tradeDiscountPercent)} / ${formatCurrency(item.tradeDiscountAmount)}`} />
                  <DetailCell value={`${formatPercent(item.specialDiscountPercent)} / ${formatCurrency(item.specialDiscountAmount)}`} />
                  <DetailCell value={formatCurrency(item.totalDiscountAmount)} strong="text-red-600" />
                  <DetailCell value={formatCurrency(item.discountedUnitCost)} strong="text-primary-800" />
                  <DetailCell value={currencyOrDash(item.mrp)} />
                  <DetailCell value={currencyOrDash(item.posPrice)} />
                  <DetailCell value={currencyOrDash(item.onlinePrice)} />
                  <DetailCell value={`${formatPercent(item.cgstRate)} / ${formatCurrency(item.cgstAmount)}`} />
                  <DetailCell value={`${formatPercent(item.sgstRate)} / ${formatCurrency(item.sgstAmount)}`} />
                  <DetailCell value={`${formatPercent(item.igstRate)} / ${formatCurrency(item.igstAmount)}`} />
                  <DetailCell value={formatPercent(item.taxRate)} strong="text-primary-800" />
                  <DetailCell value={formatCurrency(Number(item.cgstAmount || 0) + Number(item.sgstAmount || 0) + Number(item.igstAmount || 0))} />
                  <DetailCell value={formatCurrency(item.lineTotal)} strong="text-primary-800" />
                  <DetailCell value={formatCurrency(item.landedUnitCost)} />
                  <td className="px-4 py-4 text-center">
                    {grn.status === "POSTED" ? (
                      <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                        +{item.quantityAccepted} Stock
                      </span>
                    ) : (
                      <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-700">
                        Awaiting Post
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>}

      <div className="grid gap-6 lg:grid-cols-4">
        <SummaryCard icon={<FiTruck className="text-primary-700" size={20} />} title="Received Units" value={totals.received} />
        <SummaryCard icon={<FiCheckCircle className="text-green-600" size={20} />} title="Accepted Units" value={totals.accepted} />
        <SummaryCard icon={<FiAlertTriangle className="text-red-600" size={20} />} title="Rejected Units" value={totals.rejected} />
        <SummaryCard icon={<FiTruck className="text-primary-700" size={20} />} title="Stock Cost (excl. GST)" value={grn.status === 'POSTED' ? formatCurrency(totals.stockCost) : 'Not posted'} />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white">
          <h2 className="border-b p-6 text-lg font-semibold">GST breakdown by rate</h2>
          <div className="overflow-auto"><table className="w-full border-collapse text-sm [&_th]:border [&_td]:border [&_td]:border-gray-200">
            <thead className="bg-primary-800 text-white"><tr>{['GST rate','Taxable goods','CGST','SGST','IGST'].map(label=><th key={label} className="px-4 py-3 text-right">{label}</th>)}</tr></thead>
            <tbody>{totals.taxBands.map(band=><tr key={band.rate}><td className="px-4 py-3 text-right">{formatPercent(band.rate)}</td>{['taxable','cgst','sgst','igst'].map(key=><td key={key} className="px-4 py-3 text-right">{formatCurrency(band[key])}</td>)}</tr>)}</tbody>
          </table></div>
          <p className="px-6 py-4 text-xs text-gray-500">Goods tax uses the saved GRN tax amounts. Freight GST is listed separately in the payable summary.</p>
          <div className="border-t p-6"><Info label="Notes" value={grn.notes} /></div>
        </div>
        <div className="rounded-3xl border border-gray-200 bg-white p-6">
          <h2 className="mb-5 text-lg font-semibold">Invoice and payable summary</h2>
          <div className="space-y-3">
            {[
              ['Basic goods value (accepted units)',totals.basic], ['C.D. discount',-totals.cd],
              ['T.D. discount',-totals.td], ['SPCD discount',-totals.spcd],
              ['Total discount',-totals.discount], ['Taxable goods value',totals.taxable],
              ['Goods CGST',totals.cgst], ['Goods SGST',totals.sgst], ['Goods IGST',totals.igst],
              ['Goods GST total',totals.goodsTax], ['Goods total including GST',totals.goodsTotal],
              ['Freight before GST',totals.freight], ['Freight GST',totals.freightTax],
              ['Total GST including freight',totals.totalGst], ['Payable before round-off',totals.beforeRoundOff],
              ['Round-off adjustment',totals.roundOff],
            ].map(([label,value])=><div key={label} className="flex justify-between gap-4 border-b border-gray-100 pb-2 text-sm"><span className="text-gray-600">{label}</span><span className="whitespace-nowrap font-medium">{formatCurrency(value)}</span></div>)}
            <div className="flex justify-between gap-4 rounded-xl bg-primary-800 p-4 text-white"><span className="font-semibold">Amount payable after round-off</span><span className="whitespace-nowrap text-xl font-bold">{formatCurrency(totals.payable)}</span></div>
            <p className="text-xs text-gray-500">This is the GRN payable amount, not confirmation of payment to the supplier.</p>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-6 text-lg font-semibold">Activity Timeline</h2>

        <div className="space-y-5">
          <Timeline title="GRN Created" time={formatDate(grn.createdAt)} />

          {grn.status === "POSTED" && (
            <>
              <Timeline title="Inventory Updated" time={formatDate(grn.postedAt)} />
              <Timeline title="Stock Ledger Updated" time="Purchase receipt movement created" />
              <Timeline title="GRN Posted" time={formatDate(grn.postedAt)} />
            </>
          )}

          {grn.status === "DRAFT" && (
            <Timeline title="Awaiting Stock Receipt Posting" time="Draft GRN" />
          )}
        </div>
      </div>
    </div>
  );
}

function DetailCell({ value, mono = false, strong = "" }) {
  const display = value === null || value === undefined || value === "" ? "—" : value;
  return <td className={`px-4 py-4 text-xs text-gray-700 ${mono ? "font-mono" : ""} ${strong ? `font-semibold ${strong}` : ""}`}>{display}</td>;
}

function Card({ title, value }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <h3 className="mt-2 text-xl font-bold">{value || "—"}</h3>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-xs text-gray-500">{label}</p>
      <p className="font-medium">{value || "—"}</p>
    </div>
  );
}

function SummaryCard({ icon, title, value }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        {icon}
        <h3 className="font-semibold">{title}</h3>
      </div>
      <h2 className="text-3xl font-bold">{value}</h2>
    </div>
  );
}

function Timeline({ title, time }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50">
        <FiFileText className="text-primary-700" />
      </div>
      <div>
        <p className="font-medium">{title}</p>
        <p className="text-sm text-gray-500">{time}</p>
      </div>
    </div>
  );
}

function ActionButton({ icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-4"
    >
      {icon}
      {label}
    </button>
  );
}
